import { NgControl, ValidationErrors, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  QueryList,
  ViewEncapsulation,
} from '@angular/core';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { FocusMonitor } from '@angular/cdk/a11y';

import { LabelDirective } from './label.directive';
import { FormFieldErrorDirective } from './form-field-error.directive';

@Component({
  selector: 'iss-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: [ './form-field.component.scss' ],
  standalone: true,
  imports: [ NgIf, NgTemplateOutlet ],
  encapsulation: ViewEncapsulation.None,
})
export class FormFieldComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private focused = false;

  @ContentChildren(NgControl, { descendants: true }) ngControls!: QueryList<NgControl>;
  @ContentChild(FormFieldErrorDirective, { static: false }) formFieldErrorDirective?: FormFieldErrorDirective;
  @ContentChild(LabelDirective) label?: LabelDirective;

  @Input() public hint?: string;
  @Input() public showNecessity = true;

  public ngOnInit(): void {
    this.focusMonitor
      .monitor(this.elementRef, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((origin) => this.onFocusChange(!!origin));
  }

  public get required(): boolean {
    return this.ngControls.some(control => Boolean(control.control?.hasValidator(Validators.required)));
  }

  public get hintVisible(): boolean {
    return Boolean(this.hint) && this.focused && !this.errorVisible;
  }

  public get errorVisible(): boolean {
    return this.ngControls.length > 0 && this.formFieldErrorDirective?.templateRef
      ? Boolean(this.errors && this.touched)
      : false;
  }

  public get errors(): ValidationErrors | null | undefined {
    return this.firstInvalid?.errors;
  }

  public get errorsByFields(): Record<string, ValidationErrors> {
    return this.invalids?.filter(field => field.name)
      .reduce((acc, curr) => ({ ...acc, [`${String(curr.name)}`]: curr.errors }), {}) || {};
  }

  private get invalids(): NgControl[] | undefined {
    return this.ngControls.filter(control => Boolean(control.invalid));
  }

  private get firstInvalid(): NgControl | undefined {
    return this.ngControls.find(control => Boolean(control.invalid));
  }

  private get touched(): boolean {
    return this.ngControls.some(control => Boolean(control.touched));
  }

  private onFocusChange(focused: boolean): void {
    this.focused = focused;
    this.changeDetectorRef.markForCheck();
  }
}
